import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Building2, ChevronDown, ChevronUp, ChevronsUpDown, Download, Loader2, Package, Search, Warehouse } from 'lucide-react';

const InventoryGeneral = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [categories, setCategories] = useState([]);
  const [matrix, setMatrix] = useState({
    warehouses: [],
    rows: [],
    totals_by_warehouse: {},
    grand_total: 0,
  });

  const fetchMatrix = useCallback(async () => {
    try {
      const response = await api.get('/products/stock-matrix', {
        params: {
          search: search || undefined,
          sort_key: sortKey,
          sort_dir: sortDir,
        }
      });
      setMatrix(response.data);
      // Load categories for ID -> name resolution
      try {
        const catRes = await api.get('/catalog/categories');
        setCategories(catRes.data);
      } catch (_) {}
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar inventario general'));
    } finally {
      setLoading(false);
    }
  }, [search, sortKey, sortDir]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMatrix();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchMatrix]);

  const totalProducts = matrix.rows.length;
  const totalUnits = matrix.rows.reduce((acc, row) => acc + (row.total_stock || 0), 0);
  const warehousesCount = matrix.warehouses.length;

  const handleSort = (nextKey) => {
    if (sortKey === nextKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(nextKey);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <ChevronsUpDown className="w-3.5 h-3.5 ml-1 text-slate-400" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 ml-1 text-teal-600" />
      : <ChevronDown className="w-3.5 h-3.5 ml-1 text-teal-600" />;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        search: search || undefined,
        sort_key: sortKey,
        sort_dir: sortDir,
      };
      const response = await api.get('/products/stock-matrix/export', { responseType: 'blob', params });
      const contentDisposition = response.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
      const filename = match?.[1] || `inventario_general_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exportación de inventario completada');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al exportar inventario'));
    } finally {
      setExporting(false);
    }
  };

  const getCategoryName = (value) => {
    if (!value) return '-';
    const byId = categories.find(c => c.category_id === value);
    if (byId) return byId.name;
    const byName = categories.find(c => c.name === value);
    return byName ? byName.name : value;
  };

  return (
    <div className="space-y-6" data-testid="inventory-general-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">Inventario General</h1>
          <p className="text-slate-500 mt-1">Vista ejecutiva por producto y bodega con totales consolidados.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-72 max-w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por código, producto o categoría"
              className="pl-9 bg-white"
            />
          </div>
          <Button onClick={handleExport} disabled={exporting} className="bg-teal-600 hover:bg-teal-700">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exportar Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-50 text-teal-700">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Productos visibles</p>
              <p className="text-2xl font-bold text-slate-900">{totalProducts}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-700">
              <Warehouse className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Bodegas consideradas</p>
              <p className="text-2xl font-bold text-slate-900">{warehousesCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-700">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Stock total</p>
              <p className="text-2xl font-bold text-slate-900">{totalUnits}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Matriz de Existencias</CardTitle>
          <CardDescription>La tabla muestra la cantidad por bodega y total por producto.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : matrix.rows.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-slate-500">
              No hay datos para mostrar.
            </div>
          ) : (
            <ScrollArea className="w-full">
              <div className="min-w-[980px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-28 cursor-pointer select-none" onClick={() => handleSort('code')}>
                        <span className="inline-flex items-center">Código <SortIcon colKey="code" /></span>
                      </TableHead>
                      <TableHead className="w-80 cursor-pointer select-none" onClick={() => handleSort('name')}>
                        <span className="inline-flex items-center">Producto <SortIcon colKey="name" /></span>
                      </TableHead>
                      <TableHead className="w-44 cursor-pointer select-none" onClick={() => handleSort('category')}>
                        <span className="inline-flex items-center">Categoría <SortIcon colKey="category" /></span>
                      </TableHead>
                      {matrix.warehouses.map((w) => (
                        <TableHead
                          key={w.warehouse_id}
                          className="text-center min-w-[120px] cursor-pointer select-none"
                          onClick={() => handleSort(`warehouse:${w.warehouse_id}`)}
                        >
                          <span className="inline-flex items-center justify-center">
                            {w.name}
                            <SortIcon colKey={`warehouse:${w.warehouse_id}`} />
                          </span>
                        </TableHead>
                      ))}
                      <TableHead className="text-right w-32 cursor-pointer select-none" onClick={() => handleSort('total_stock')}>
                        <span className="inline-flex items-center justify-end">Total <SortIcon colKey="total_stock" /></span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matrix.rows.map((row) => (
                      <TableRow key={`${row.code}-${row.name}`}>
                        <TableCell className="font-mono text-xs text-slate-600">{row.code}</TableCell>
                        <TableCell className="font-medium text-slate-900">{row.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{getCategoryName(row.category)}</Badge>
                        </TableCell>
                        {matrix.warehouses.map((w) => (
                          <TableCell key={`${row.code}-${w.warehouse_id}`} className="text-center font-semibold text-slate-700">
                            {row.by_warehouse?.[w.warehouse_id] ?? 0}
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-bold text-slate-900">{row.total_stock}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-slate-100/60">
                      <TableCell />
                      <TableCell className="font-bold text-slate-900">TOTAL GENERAL</TableCell>
                      <TableCell />
                      {matrix.warehouses.map((w) => (
                        <TableCell key={`total-${w.warehouse_id}`} className="text-center font-bold text-slate-900">
                          {matrix.totals_by_warehouse?.[w.warehouse_id] ?? 0}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-bold text-slate-900">{matrix.grand_total}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryGeneral;
